import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { fetchData, patchData, postData } from "../utils/api";
import { useParams, useSearchParams } from "react-router-dom";
import Header from "../components/marketing/header.js";
import CalendarEvent from "../components/event/CalendarEvent";
import DefaultEvent from "../components/event/DefaultEvent";
import EventCalendarCompaign from "../components/event/EventCalendarCompaign";
import Loader from "../components/Loader.js";
import { toast } from 'react-toastify';
import { MARKETING_CHANNELS } from "../config/marketingConfig";
import EventPressReleaseCampaign from "../components/event/EventPressReleaseCampaign";
import useFeatureGates from "../hooks/useFeatureGates";
import { isPressReleaseV2Enabled, buildPressReleaseAppUrl } from "../utils/pressReleaseAuth";

const MarketingEventPage = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const eventId = searchParams.get("event_id");
  const clearCart = searchParams.get("clear_cart");
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [selectedFeature, setSelectedFeature] = useState({});
  const [eventPrice, setEventPrice] = useState({});
  const [totalPrice, setTotalPrice] = useState(0);
  const [dateRange, setDateRange] = useState({});
  const [selectedImplementation, setSelectedImplementation] = useState({});
  const [listSize, setListSize] = useState({});
  const [loading, setLoading] = useState(false);

  const [isEventDeselect, setIsEventDeselect] = useState(false)
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false)

  const [hasInitialized, setHasInitialized] = useState(false);
  const [activeIndex, setActiveIndex] = useState(1);

  // Press Release v2: a direct hit on /marketing/press_release_ai (bookmark, old link) leaves for
  // the external Press Release app once the company gates resolve. Gate OFF renders the legacy flow.
  const { featureGates, gatesLoading } = useFeatureGates('company');
  const pressReleaseV2 = id === 'press_release_ai' && !gatesLoading && isPressReleaseV2Enabled(featureGates);
  useEffect(() => {
    if (!pressReleaseV2) return;
    const url = buildPressReleaseAppUrl();
    if (url) window.location.href = url;
  }, [pressReleaseV2]);

  // Min date
  const today = new Date();
  const alertConfigurations = {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
      }
  today.setDate(today.getDate() + 2);
  const pad = (n) => n.toString().padStart(2, '0');
  const minDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}T${pad(today.getHours())}:${pad(today.getMinutes())}`;
  const [selectedDate, setSelectedDate] = useState({});

  const recalculateTotalPrice = (eventPriceObj) => {
    const total = Object.values(eventPriceObj).reduce((sum, p) => sum + (p || 0), 0);
    return total;
  };

  const handleFeatureSelect = (eventId, featureType, isRemoved = false, event = {}) => {
    if (isRemoved) {
      setSelectedFeature((prev) => {
        const updated = { ...prev };
        if (featureType && updated[eventId]) {
          updated[eventId] = updated[eventId].filter((feature) => feature !== featureType);
          if (updated[eventId].length === 0) delete updated[eventId];
        } else if (!featureType) {
          delete updated[eventId];
        }
        return updated;
      });
      setDateRange((prev) => {
        const updated = { ...prev };
        if (featureType) {
          Object.keys(updated).forEach((key) => {
            if (key === `${eventId}-${featureType}` || key.startsWith(`${eventId}-${featureType}-`)) {
              delete updated[key];
            }
          });
        } else {
          Object.keys(updated).forEach((key) => {
            if (key.startsWith(`${eventId}-`)) {
              delete updated[key];
            }
          });
        }
        return updated;
      });
      setEventPrice((prev) => {
        const updated = { ...prev };
        if (featureType) {
          Object.keys(updated).forEach((key) => {
            if (key === `${eventId}-${featureType}` || key.startsWith(`${eventId}-${featureType}-`)) {
              delete updated[key];
            }
          });
        } else {
          Object.keys(updated).forEach((key) => {
            if (key.startsWith(`${eventId}-`)) {
              delete updated[key];
            }
          });
        }
        setTotalPrice(recalculateTotalPrice(updated));
        return updated;
      });
      if (featureType) {
        let [impl, implId] = featureType.split('-');
        const data = {
          event_id: JSON.stringify(eventId),
          is_remove: true,
          is_selected: false,
          from_bulk_boost: true,
        };
        if (implId && !isNaN(Number(implId))) {
          data.implementation = impl;
          data.implementation_id = implId;
        }

        mutation.mutate({
          endpoint: `cart/boosts/${implId ? impl + 's' : featureType.replace(/-.*/, '') + 's'}`,
          data,
        });
      }
      return;
    }
    setSelectedFeature((prev) => ({
      ...prev,
      [eventId]: [...(prev[eventId] || []), featureType],
    }));
  };

  const handleSelectDate = (eventId, implementation, isRemoved = false) => {
    if (isRemoved) {
      setSelectedImplementation(prev => {
        const updated = { ...prev };
        delete updated[eventId];
        return updated;
      });

      setSelectedDate(prev => {
        const updated = Object.fromEntries(
          Object.entries(prev).filter(([key]) => !key.startsWith(`${eventId}-`))
        );
        return updated;
      });

      setListSize(prev => {
        const updated = Object.fromEntries(
          Object.entries(prev).filter(([key]) => !key.startsWith(`${eventId}-`))
        );
        return updated;
      });

      return;
    }

    setSelectedImplementation(prev => ({
      ...prev,
      [eventId]: implementation,
    }));

    setSelectedDate(prev => ({
      ...Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith(`${eventId}-`))),
      [`${eventId}-${implementation}`]: "",
    }));

    setListSize(prev => ({
      ...Object.fromEntries(Object.entries(prev).filter(([key]) => !key.startsWith(`${eventId}-`))),
      [`${eventId}-${implementation}`]: "",
    }));


    setEventPrice(prev => {
      const updated = { ...prev };
      if (updated[eventId]) {
        const priceToSubtract = parseFloat(updated[eventId]);
        setTotalPrice(prevTotal => prevTotal - priceToSubtract);
        delete updated[eventId];
      }
      return updated;
    });
  };

  const boostEventMutation = useMutation({
    mutationFn: ({endpoint, data}) => postData(endpoint, data),
    onSuccess: (event) => {
      setLoading(false)
      if (event?.success && event?.redirect_url) {
        window.location.href = event.redirect_url;
      } else if (event?.errors) {
        console.error(event.errors);
        toast.error(event.errors.join('\n'), alertConfigurations);
      }
    },
    onError: (error) => {
      setLoading(false)
      console.error(error);
      toast.error('Cannot submit your events. Please try again later!', alertConfigurations);
    },
  });

  const BoostEventButton = ({ selectedEvents }) => {
    if (selectedEvents?.length > 98) {
      toast.error('Limit exceeded. Please select a maximum of 98 events.', alertConfigurations);
      return;
    }
    const isCalendarType = selectedEvent?.every(event => event.service_type === 'calendar');
    const expectedLength = isCalendarType
      ? Object.values(selectedFeature).reduce((acc, arr) => acc + (arr ? arr.length : 0), 0)
      : Object.keys(listSize)?.length;
    if (isCalendarType) {
      const allDateRangesValid = Object.entries(dateRange).every(
        ([key, range]) => Array.isArray(range) && range.length === 2 && range[0] && range[1]
      );
      if (!allDateRangesValid) {
        toast.error('Date ranges against some events are not selected!', alertConfigurations);
        return;
      }
    }

    if (selectedEvents?.length !== expectedLength) {
      const errorMessage = isCalendarType
        ? 'Date ranges against some events are not selected!'
        : 'Quantities against some events are not selected!';
      toast.error(errorMessage, alertConfigurations);
      return;
    }
    if (selectedEvents.length == 0) {
      toast.error('Please select events first!', alertConfigurations);
      return;
    }
    setLoading(true);
    boostEventMutation.mutate({
      endpoint: 'events/bulk_boosts',
      data: { selectedEvents, id }
    });
  };

  const { data, isLoading, isError, dataUpdatedAt, isFetching } = useQuery({
    queryKey: id ? ["marketing-event", id] : null,
    queryFn: () => fetchData(`api/marketing/${id}?event_id=${eventId}&clear_cart=${clearCart || false}`),
    enabled: !!id,
    keepPreviousData: false,
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: 'always',
  });

  const eventsItems = data?.events || [];

  useEffect(() => {
    const isClearCart = data?.is_clear_cart || false;
    if (isClearCart) {
      const newParams = new URLSearchParams(searchParams.toString());
      newParams.delete("clear_cart");
      newParams.delete("bulk_cart_event_ids");

      setSearchParams(newParams);
    }
  }, [data]);

  useEffect(() => {
    return () => queryClient.removeQueries(['marketing-event', id]);
  }, [id]);  

  const mutation = useMutation({
    mutationFn: ({ endpoint, data }) => patchData(endpoint, data),
    onSuccess: (data, variables) => {
      if (variables?.data?.is_remove) {
        if (variables.data.implementation && variables.data.implementation_id) {
          const featureKey = `${variables.data.event_id}-${variables.data.implementation}-${variables.data.implementation_id}`;
          setEventPrice((prev) => {
            const updated = { ...prev };
            if (updated[featureKey]) {
              delete updated[featureKey];
            }
            setTotalPrice(recalculateTotalPrice(updated));
            return updated;
          });
        }
        if(isEventDeselect) {
          setIsEventDeselect(false)
        } else {
          setSelectedEvent((prevEvents) => {
            const updatedEvents = prevEvents.filter((event) => {
              const featuresForEvent = selectedFeature[event.id] || [];
              return featuresForEvent;
            });
            return updatedEvents;
          });
        }
      } else {
        setSelectedEvent((prev) => {
          const newEvent = eventsItems.find((event) => event.id == variables.data.event_id);
          const activeCalendarEvent = MARKETING_CHANNELS[id]?.implementations.find(
            (i) => newEvent?.[i]?.id == variables.data?.calendar_id
          );

          if (newEvent && !prev.some((e) => e.id == newEvent.id)) {
            return [
              ...prev,
              {
                ...newEvent,
                channel: activeCalendarEvent,
                [activeCalendarEvent]: {
                  ...newEvent[activeCalendarEvent],
                  is_selected: true,
                },
              },
            ];
          }

          return prev.map((event) =>
            event.id == variables.data.event_id
              ? {
                  ...event,
                  channel: activeCalendarEvent,
                  [activeCalendarEvent]: {
                    ...event[activeCalendarEvent],
                    is_selected: true,
                  },
                }
              : event
          );
        });

      }
    },
    onError: (error) => {
      console.error("Failed to update email blast:", error);
    },
  });

  useEffect(() => {
    if (!data || hasInitialized) return;
    const now = Date.now();
    const isFresh = now - dataUpdatedAt < 500;
    if (!isFresh) return;
    setHasInitialized(true);

    if (data?.persisted_items?.length > 0) {
      const keys = Object.keys(JSON.parse(data.persisted_items));
      const persistedEvents = data.events.filter((ev) =>
        keys.some((i) => i == ev.id)
      );

      const newSelectedImplementations = {};
      const newSelectedFeature = {};
      const newEventPrice = {};
      const newDateRange = {};
      const newListSize = {};
      let newTotalPrice = 0;

      persistedEvents.forEach((event) => {
        const selectedFeatures = [];
        const implementations = MARKETING_CHANNELS[id]?.implementations || [];
        implementations.forEach((implementation) => {
          const implData = event[implementation];
          if (Array.isArray(implData)) {
            implData.forEach((implItem) => {
              if (implItem?.is_selected) {
                const featureKey = `${event.id}-${implementation}-${implItem.id}`;
                selectedFeatures.push(`${implementation}-${implItem.id}`);
                const price = implItem?.calculated_price || 0;
                newEventPrice[featureKey] = price;
                newTotalPrice += price;
                newDateRange[featureKey] = [
                  implItem?.start_date ? new Date(implItem.start_date) : null,
                  implItem?.end_date ? new Date(implItem.end_date) : null,
                ];
              }
            });
          } else if (implData && typeof implData === 'object' && implData.is_selected) {
            // Legacy single object
            const featureKey = `${event.id}-${implementation}`;
            selectedFeatures.push(implementation);
            const price = implData?.calculated_price || 0;
            newEventPrice[featureKey] = price;
            newTotalPrice += price;
            newDateRange[featureKey] = [
              implData?.start_date ? new Date(implData.start_date) : null,
              implData?.end_date ? new Date(implData.end_date) : null,
            ];
          }
        });
        if (selectedFeatures.length > 0) {
          newSelectedFeature[event.id] = selectedFeatures;
          newSelectedImplementations[event.id] = selectedFeatures;
          newListSize[`${event.id}-${selectedFeatures}`] = event[selectedFeatures]?.size || "";
        } else {
          // fallback for legacy
          newEventPrice[event.id] = event?.calculated_price || 0;
          newTotalPrice += event.calculated_price;
        }
      });

      let combinedEvents = [...persistedEvents];
      const boostingEvent = data?.boosting_event;
      const isBoostingEventPersisted = boostingEvent && persistedEvents.some(e => e.id === boostingEvent.id);

      if (boostingEvent && !isBoostingEventPersisted) {
        combinedEvents.push(boostingEvent);
      } else if (eventId && !isBoostingEventPersisted) {
        toast.error("Boosting event is not present!", alertConfigurations);
      }

      setSelectedEvent(combinedEvents);
      setSelectedImplementation(newSelectedImplementations);
      setSelectedFeature((prev) => ({ ...prev, ...newSelectedFeature }));
      setEventPrice((prev) => ({ ...prev, ...newEventPrice }));
      setDateRange((prev) => ({ ...prev, ...newDateRange }));
      setListSize((prev) => ({ ...prev, ...newListSize }));
      setTotalPrice(newTotalPrice);
    }

    return () => {
      setSelectedEvent([]);
      setSelectedImplementation({});
      setSelectedFeature({});
      setSelectedDate({})
      setEventPrice({});
      setDateRange({});
      setListSize({});
      setTotalPrice(0);
    };
  }, [data, dataUpdatedAt]);

  const handleEventCard = (event, isRemove = false) => {
    setSelectedEvent((prevEvents) => {
      if (isRemove) {
        setEventPrice((prev) => {
          const updated = { ...prev };
          Object.keys(updated).forEach((key) => {
            if (key.startsWith(`${event.id}-`)) {
              delete updated[key];
            }
          });
          setTotalPrice(recalculateTotalPrice(updated));
          return updated;
        });

        calculatePrice(event, null, true, event?.channel ?? event?.feature_types[0], null, null, null, true, true, true);
        handleFeatureSelect(event.id, null, true);
        handleSelectDate(event.id, null, true);
        return prevEvents.filter((ev) => ev.id !== event.id);
      } else {
        const isAlreadySelected = prevEvents.some((ev) => ev.id === event.id);
        if (selectedEvent?.length >= 98 && !isAlreadySelected) {
          toast.error('Limit exceeded. Please select a maximum of 98 events.', alertConfigurations);
          return prevEvents;
        }
        return isAlreadySelected ? prevEvents : [...prevEvents, event];
      }
    });
  };

  const calculatePrice = (event, size, isRemove = false, path, startDate, endDate, calendarId, isHideToast = true, sidebar = false, remove_whole_event = false) => {
    setEventPrice((prev) => {
      const updatedEventPrice = { ...prev };
      // Always use full key: eventId-featureType-implId if calendarId exists
      let featureKey = `${event.id}-${path?.replace(/s$/, "")}`;
      if (calendarId) {
        featureKey += `-${calendarId}`;
      }
      if (isRemove) {
        const priceToSubtract = updatedEventPrice[featureKey] || 0;
        setTotalPrice((prevTotal) => prevTotal - priceToSubtract);
        delete updatedEventPrice[featureKey];
      } else {
        const newPrice = size * event.price;
        updatedEventPrice[featureKey] = newPrice;
        const total = Object.values(updatedEventPrice).reduce((sum, p) => sum + p, 0);
        setTotalPrice(total);
      }
      return updatedEventPrice;
    });
    if (!path || typeof path !== "string") return;
    const newPath = path?.endsWith('s') ? path : `${path}s`;
    if (!newPath) return;
    mutation.mutate(
      {
        endpoint: `cart/boosts/${newPath}`,
        data: {
          event_id: JSON.stringify(event.id),
          number_of_subscribers: size,
          sidebar: sidebar,
          from_bulk_boost: true,
          ...(event.date || selectedDate ? { date: event.date || selectedDate } : {}),
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          ...(calendarId ? { calendar_id: calendarId } : {}),
          ...(isRemove ? { is_remove: true } : {}),
          ...(remove_whole_event ? { remove_whole_event: true } : {}),

        },
      },
      {
        onSuccess: (response) => {
          if (!response.success) {
            console.error("API responded with success: false", response);
            toast.error('Something went wrong while updating the event. Please try again later!', alertConfigurations);
          }
        },
        onError: (error) => {
          console.error("API error:", error);
          toast.error(error?.response?.data?.error || 'Failed to update the event. Please check your internet connection or try again later.', alertConfigurations);
        },
      }
    );
  };

  if (isLoading || isFetching || pressReleaseV2) return <Loader />;

  if (isError)
    return (
      <div className="error-container">
        <p>Failed to load event data.</p>
      </div>
    );

  const renderCampaign = (id) => {
    if (id === 'event_calendars') {
      return <EventCalendarCompaign eventsItems={eventsItems} />;
    }

    return (
        <EventPressReleaseCampaign
            eventsItems={eventsItems}
            activeIndex={activeIndex}
            setActiveIndex={setActiveIndex}
        />
    );
  };

  return (
    <div className="marketing">
      <Header activeIndex={activeIndex} setActiveIndex={setActiveIndex} />
      {(id === 'event_calendars' || id === 'press_release_ai') ? (
          renderCampaign(id)
      ) : (
        <div className="marketing__content cts-container">
          <div className="d-flex justify-content-between align-items-center flex-wrap">
            <h2 className="feature-heading">{`${id.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")} Campaign`}</h2>
            <div className="d-flex align-items-center ml-auto">
              <div className="text-center feature-heading-two h4 mb-0 mr-2">Total: ${totalPrice || 0}</div>
              <button
                className={`review-button ${isDatePickerOpen ? 'disabled' : ''}`}
                onClick={() => {
                  let selectedEvents;
                  if (id === 'featured_placements' && selectedEvent?.every(event => event.service_type === 'calendar')) {
                    selectedEvents = selectedEvent.flatMap((event) => {
                      const features = selectedFeature[event.id] || [];
                      return features.map((featureType) => {
                        const [impl, implId] = featureType.split('-');
                        const featureKey = `${event.id}-${impl}-${implId}`;
                        const range = dateRange[featureKey] || [null, null];
                        return {
                          id: event.id,
                          channel: impl,
                          calendar_id: implId,
                          start_date: range[0],
                          end_date: range[1],
                        };
                      });
                    });
                  } else {
                    selectedEvents = selectedEvent.flatMap((event) => {
                      return [{ id: event.id, channel: event.channel }];
                    });
                  }
                  BoostEventButton({
                    totalPrice: totalPrice,
                    selectedEvents,
                    channel: id
                  });
                }}
                disabled={loading || isDatePickerOpen}
              >
                {loading ? "Submitting..." : "Review/Submit"}
              </button>
            </div>
          </div>

          <div className="marketing__content__events">
            {/* Sidebar start */}

            { (!eventsItems || eventsItems.length === 0) ? (
               <div className="marketing__content__events__sidebar">
                <div className="event-header">
                 <span className="title text-center">All events are already promoted to the {id.split("_").join(" ")}.</span>
                </div>
                </div>
              ) : (
                <div className="marketing__content__events__sidebar">
                  <div className="event-header">
                    <h2 className="title">Selected Events: {selectedEvent?.length}</h2>
                  </div>
                  <div className="events_container">
                    {eventsItems.map((event, index) => (
                      <div
                        className={`events_container__item ${selectedEvent.some((st) => st.id === event.id) ? "active" : ""}`}
                        key={index}
                        onClick={() => handleEventCard(event, selectedEvent.some((st) => st.id === event.id))}
                      >
                        <img
                          src={event.banner_image}
                          alt="event"
                          width="76"
                          height="46"
                        />
                        <div>
                          <h4 className="text-ellipse">{event.name}</h4>
                          <span>{event.event_address}</span> |{" "}
                          <span className="ml-1">{event?.start_date}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )

            }
            {/* Sidebar end */}

            <div className="event-card-container">
              {selectedEvent?.map((event, index) => (
                <div className="marketing__content__events__card" key={index}>
                    {event?.service_type === 'calendar'
                      ?
                        <CalendarEvent
                          event={event}
                          dateRange={dateRange}
                          eventPrice={eventPrice}
                          setDateRange={setDateRange}
                          setEventPrice={setEventPrice}
                          setTotalPrice={setTotalPrice}
                          calculatePrice={calculatePrice}
                          handleEventCard={handleEventCard}
                          selectedFeature={selectedFeature}
                          handleFeatureSelect={handleFeatureSelect}
                          minDate={minDate}
                          implementations={MARKETING_CHANNELS[id]?.implementations}
                          setIsDatePickerOpen={setIsDatePickerOpen}
                        />
                      :
                        <DefaultEvent
                          event={event}
                          eventPrice={eventPrice}
                          selectedDate={selectedDate}
                          calculatePrice={calculatePrice}
                          handleEventCard={handleEventCard}
                          setSelectedDate={setSelectedDate}
                          minDate={minDate}
                          listSize={listSize}
                          setListSize={setListSize}
                          handleSelectDate={handleSelectDate}
                          selectedImplementation={selectedImplementation}
                          implementations={MARKETING_CHANNELS[id]?.implementations}
                        />
                    }
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MarketingEventPage;
